import actionTypes from 'constants/actionTypes'
import { selectNeighborhood, selectFocus } from 'selectors/graph'
import { resetDepth } from 'actions/graph'
import type { MiddlewareStoreShape, NextFn } from 'types/adapters'

type RootState = import('types/state').RootState
type NeighborhoodResult = import('types/domain').NeighborhoodResult

type FocusAction = {
  type?: string
  payload?: {
    id?: string
  }
}

type MiddlewareStore = MiddlewareStoreShape<RootState>

const cloneForState = <T extends Record<string, unknown>>(obj: T | null | undefined): Record<string, unknown> => {
  if (!obj) {
    return {}
  }
  return Object.assign({}, obj)
}

const sanitizeEntity = (entity: unknown) => {
  if (entity == null || typeof entity !== 'object') {
    return entity
  }
  const e = Object.assign({}, entity as Record<string, unknown>)
  delete e.neighbors
  delete e.more
  delete e.moreEdges
  // delete e.depth
  return e
}

const clearFocusedFlag = (entity: unknown) => {
  if (entity == null || typeof entity !== 'object') {
    return entity
  }
  // Avoid mutating selector output/state references
  const e = Object.assign({}, entity as Record<string, unknown>)
  delete e.focused
  delete e.editing
  return e
}

const sanitizeNeighborhoodSnapshot = (neighborhood: NeighborhoodResult, previousFocusId?: string) => {
  const snapshot: {
    nodes: Record<string, unknown>
    edges: Record<string, unknown>
  } = {
    nodes: cloneForState(neighborhood?.nodes),
    edges: cloneForState(neighborhood?.edges),
  }
  if (snapshot.nodes != null) {
    for (const id in snapshot.nodes) {
      snapshot.nodes[id] = sanitizeEntity(snapshot.nodes[id])
    }
  }
  if (snapshot.edges != null) {
    for (const id in snapshot.edges) {
      snapshot.edges[id] = sanitizeEntity(snapshot.edges[id])
    }
  }
  if (previousFocusId) {
    if (snapshot.nodes?.[previousFocusId] != null) {
      snapshot.nodes[previousFocusId] = clearFocusedFlag(snapshot.nodes[previousFocusId])
    }
    if (snapshot.edges?.[previousFocusId] != null) {
      snapshot.edges[previousFocusId] = clearFocusedFlag(snapshot.edges[previousFocusId])
    }
  }
  return snapshot
}

export default (store: MiddlewareStore) => (next: NextFn<FocusAction>) => (action: FocusAction) => {
  if (action.type === actionTypes.GRAPH.FOCUS) {
    const state = store.getState()
    const f = selectFocus(state)
    const previousFocusId = state.graph?.focus?.id
    const nextFocusId = action.payload?.id
    if (f != null && f.id && nextFocusId && f.id !== nextFocusId) {
      const neighborhood = selectNeighborhood(state)
      const capturedAtMs = Date.now()
      store.dispatch({
        type: actionTypes.GRAPH.PREVIOUS_NEIGHBORHOODS.CAPTURE,
        payload: {
          capturedAtMs,
          neighborhood: sanitizeNeighborhoodSnapshot(neighborhood, previousFocusId),
        },
      })
      store.dispatch(resetDepth())

      setTimeout(() => {
        store.dispatch({
          type: actionTypes.GRAPH.PREVIOUS_NEIGHBORHOODS.REMOVE,
          payload: { capturedAtMs },
        })
      }, 2500)
    }
  }
  return next(action)
}
